import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';

async function fetchJson<T>(url: string): Promise<T> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`API error: ${res.status}`);
  return res.json() as Promise<T>;
}

// ── Types ──

export interface LoadedArea {
  id: number;
  fips5: string;
  state_abbr: string;
  county_name: string;
  source_type: string;
  format: string;
  status: string;
  parcel_count: number | null;
  actual_count: number;
  last_success_at: string | null;
  country: string;
}

export interface StatsResponse {
  totalParcels: number;
  totalSources: number;
  countryCount: number;
  sourcesByStatus: { status: string; count: number }[];
  recentCrawlRuns: CrawlRun[];
  loadedAreas: LoadedArea[];
}

export interface CrawlRun {
  id: number;
  source_id: number;
  state_abbr: string;
  county_name: string;
  country: string;
  status: string;
  parcels_found: number | null;
  parcels_new: number | null;
  parcels_errors: number | null;
  started_at: string;
  completed_at: string | null;
  duration_secs: number | null;
}

export interface CrawlsResponse {
  crawlRuns: CrawlRun[];
  total: number;
  page: number;
  limit: number;
}

export interface Source {
  id: number;
  fips5: string;
  state_abbr: string;
  county_name: string;
  country: string;
  source_type: string;
  source_url: string;
  format: string;
  status: string;
  parcel_count: number | null;
  last_crawled_at: string | null;
  last_success_at: string | null;
  error_message: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface SourcesResponse {
  sources: Source[];
  total: number;
  page: number;
  limit: number;
}

export interface Parcel {
  id: number;
  fips5: string;
  apn_raw: string | null;
  apn_normalized: string | null;
  address_full: string | null;
  city: string | null;
  state_abbr: string | null;
  zip5: string | null;
  owner_name: string | null;
  owner_type: string | null;
  land_use_category: string | null;
  assessed_value_total: number | null;
  year_built: number | null;
  building_sqft: number | null;
  lot_size_sqft: number | null;
  source_type: string | null;
  created_at: string;
  upi: string | null;
  [key: string]: unknown;
}

export interface ParcelsResponse {
  parcels: Parcel[];
  total: number | null;
  hasMore: boolean;
  page: number;
  limit: number;
}

export interface StateCoverage {
  state_abbr: string;
  total_counties: number;
  total_parcels: number;
  total_housing_units: number;
  avg_coverage: number;
  with_state_bulk: number;
  with_county_gis: number;
}

export interface CoverageResponse {
  states: StateCoverage[];
  counties: unknown[] | null;
}

// ── Hooks ──

export function useStats() {
  return useQuery({
    queryKey: ['stats'],
    queryFn: () => fetchJson<StatsResponse>('/api/stats'),
    staleTime: 30_000,
  });
}

export function useSources(params: { status?: string; state?: string; page?: number; sort?: string; order?: string }) {
  const sp = new URLSearchParams();
  if (params.status) sp.set('status', params.status);
  if (params.state) sp.set('state', params.state);
  sp.set('page', String(params.page ?? 1));
  if (params.sort) sp.set('sort', params.sort);
  if (params.order) sp.set('order', params.order);
  return useQuery({
    queryKey: ['sources', params],
    queryFn: () => fetchJson<SourcesResponse>(`/api/sources?${sp}`),
    placeholderData: keepPreviousData,
  });
}

export function useParcels(params: { q?: string; state?: string; city?: string; page?: number; sort?: string; order?: string }) {
  const sp = new URLSearchParams();
  if (params.q) sp.set('q', params.q);
  if (params.state) sp.set('state', params.state);
  if (params.city) sp.set('city', params.city);
  sp.set('page', String(params.page ?? 1));
  if (params.sort) sp.set('sort', params.sort);
  if (params.order) sp.set('order', params.order);
  return useQuery({
    queryKey: ['parcels', params],
    queryFn: () => fetchJson<ParcelsResponse>(`/api/parcels?${sp}`),
    placeholderData: keepPreviousData,
  });
}

export function useCoverage(state?: string) {
  const url = state ? `/api/coverage?state=${state}` : '/api/coverage';
  return useQuery({
    queryKey: ['coverage', state],
    queryFn: () => fetchJson<CoverageResponse>(url),
    staleTime: 60_000,
  });
}

export function useCrawls(params: { page?: number; sort?: string; order?: string }) {
  const sp = new URLSearchParams();
  sp.set('page', String(params.page ?? 1));
  if (params.sort) sp.set('sort', params.sort);
  if (params.order) sp.set('order', params.order);
  return useQuery({
    queryKey: ['crawls', params],
    queryFn: () => fetchJson<CrawlsResponse>(`/api/crawls?${sp}`),
    placeholderData: keepPreviousData,
  });
}

// ── Court Types ──

export interface CourtCase {
  id: number;
  integra_court_id: string;
  court_code: string;
  jurisdiction: string;
  court_level: string;
  case_number: string;
  case_type: string;
  case_subtype: string | null;
  nature_of_suit: string | null;
  case_name: string | null;
  date_filed: string | null;
  date_terminated: string | null;
  disposition: string | null;
  plaintiff: string | null;
  defendant: string | null;
  judge: string | null;
  state_abbr: string | null;
  county_name: string | null;
  source_type: string;
  source_url: string | null;
  source_case_id: string | null;
  created_at: string;
}

export interface CourtCasesResponse {
  cases: CourtCase[];
  total: number;
  hasMore: boolean;
  page: number;
  limit: number;
}

export interface CourtStatsResponse {
  totalCases: number;
  totalCourts: number;
  totalSources: number;
  casesByType: { case_type: string; count: number }[];
  casesByJurisdiction: { jurisdiction: string; count: number }[];
  recentCases: CourtCase[];
}

// ── Court Hooks ──

export function useCourtCases(params: {
  q?: string;
  state?: string;
  case_type?: string;
  court_code?: string;
  page?: number;
  sort?: string;
  order?: string;
}) {
  const sp = new URLSearchParams();
  if (params.q) sp.set('q', params.q);
  if (params.state) sp.set('state', params.state);
  if (params.case_type) sp.set('case_type', params.case_type);
  if (params.court_code) sp.set('court_code', params.court_code);
  sp.set('page', String(params.page ?? 1));
  if (params.sort) sp.set('sort', params.sort);
  if (params.order) sp.set('order', params.order);
  return useQuery({
    queryKey: ['court-cases', params],
    queryFn: () => fetchJson<CourtCasesResponse>(`/api/courts/cases?${sp}`),
    placeholderData: keepPreviousData,
  });
}

export function useCourtStats() {
  return useQuery({
    queryKey: ['court-stats'],
    queryFn: () => fetchJson<CourtStatsResponse>('/api/courts/stats'),
    staleTime: 30_000,
  });
}

// ── Court Source / Crawl / Coverage Types ──

export interface CourtSource {
  id: number;
  court_id: number;
  court_code: string;
  court_name: string;
  jurisdiction: string;
  court_level: string;
  state_abbr: string | null;
  source_type: string;
  source_url: string;
  format: string | null;
  platform: string | null;
  scope: string | null;
  status: string;
  record_count: number | null;
  last_crawled_at: string | null;
  last_success_at: string | null;
  error_message: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface CourtSourcesResponse {
  sources: CourtSource[];
  total: number;
  page: number;
  limit: number;
}

export interface CourtCrawlRun {
  id: number;
  source_id: number;
  court_name: string;
  court_code: string;
  jurisdiction: string;
  state_abbr: string | null;
  status: string;
  cases_found: number | null;
  cases_new: number | null;
  cases_updated: number | null;
  cases_errors: number | null;
  started_at: string;
  completed_at: string | null;
  duration_secs: number | null;
  error_message: string | null;
}

export interface CourtCrawlsResponse {
  crawlRuns: CourtCrawlRun[];
  total: number;
  page: number;
  limit: number;
}

export interface CourtCoverage {
  id: number;
  court_code: string;
  name: string;
  short_name: string | null;
  jurisdiction: string;
  level: string;
  court_type: string;
  state_abbr: string | null;
  our_case_count: number | null;
  est_annual_cases: number | null;
  coverage_pct: number | null;
  has_bulk_source: boolean;
  has_api_source: boolean;
  quality_score: number | null;
  last_updated_at: string | null;
}

export interface CourtCoverageResponse {
  courts: CourtCoverage[];
}

// ── Court Source / Crawl / Coverage Hooks ──

export function useCourtSources(params: { status?: string; page?: number; sort?: string; order?: string }) {
  const sp = new URLSearchParams();
  if (params.status) sp.set('status', params.status);
  sp.set('page', String(params.page ?? 1));
  if (params.sort) sp.set('sort', params.sort);
  if (params.order) sp.set('order', params.order);
  return useQuery({
    queryKey: ['court-sources', params],
    queryFn: () => fetchJson<CourtSourcesResponse>(`/api/courts/sources?${sp}`),
    placeholderData: keepPreviousData,
  });
}

export function useCourtCrawls(params: { page?: number; sort?: string; order?: string }) {
  const sp = new URLSearchParams();
  sp.set('page', String(params.page ?? 1));
  if (params.sort) sp.set('sort', params.sort);
  if (params.order) sp.set('order', params.order);
  return useQuery({
    queryKey: ['court-crawls', params],
    queryFn: () => fetchJson<CourtCrawlsResponse>(`/api/courts/crawls?${sp}`),
    placeholderData: keepPreviousData,
  });
}

export function useCourtCoverage() {
  return useQuery({
    queryKey: ['court-coverage'],
    queryFn: () => fetchJson<CourtCoverageResponse>('/api/courts/coverage'),
    staleTime: 60_000,
  });
}

export function useTriggerSource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sourceId: number) =>
      fetch(`/api/sources/${sourceId}/trigger`, { method: 'POST' }).then(r => r.json()),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['sources'] });
      void qc.invalidateQueries({ queryKey: ['stats'] });
    },
  });
}

// ── Record Source / Refresh Types ──

export interface RecordSourceInfo {
  found: boolean;
  source: {
    id: number;
    registry: string;
    recordId: number;
    methodName: string;
    methodType: string;
    methodStatus: string;
    sourceRecordUrl: string | null;
    dataHash: string | null;
    lastVerifiedAt: string | null;
    lastRefreshStatus: string;
    pendingChanges: number;
  } | null;
}

export interface FieldChange {
  field: string;
  oldValue: string | null;
  newValue: string | null;
  changeType: 'added' | 'modified' | 'removed';
}

export interface RefreshResult {
  status: 'unchanged' | 'changed' | 'error' | 'unavailable';
  cached: Record<string, unknown>;
  live: Record<string, unknown> | null;
  diff: FieldChange[];
  verifiedAt: string;
  errorMessage?: string;
}

export interface ScrapingMethod {
  id: number;
  name: string;
  display_name: string;
  method_type: string;
  registry: string;
  status: string;
  record_count: number;
  changed_count: number;
  rate_limit_rpm: number | null;
  rate_limit_rpd: number | null;
  cost_per_request: number;
  requires_proxy: boolean;
  response_format: string;
  last_tested_at: string | null;
}

export interface MethodsResponse {
  data: ScrapingMethod[];
  total: number;
}

// ── Record Source / Refresh Hooks ──

export function useRecordSource(registry: string, recordId: number | null) {
  return useQuery({
    queryKey: ['record-source', registry, recordId],
    queryFn: () =>
      fetchJson<RecordSourceInfo>(
        `/api/record-source?registry=${registry}&recordId=${recordId}`,
      ),
    enabled: !!recordId && recordId > 0,
    staleTime: 60_000,
  });
}

export function useRefreshRecord() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ registry, recordId }: { registry: string; recordId: number }) =>
      fetch(`/api/refresh/${registry}/${recordId}`, { method: 'POST' }).then(
        (r) => r.json() as Promise<RefreshResult>,
      ),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({
        queryKey: ['record-source', variables.registry, variables.recordId],
      });
    },
  });
}

export function useAcceptChanges() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (changeIds: number[]) =>
      fetch('/api/refresh/accept', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ changeIds }),
      }).then((r) => r.json()),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['record-source'] });
    },
  });
}

export function useMethods(registry?: string) {
  const sp = new URLSearchParams();
  if (registry) sp.set('registry', registry);
  return useQuery({
    queryKey: ['methods', registry],
    queryFn: () => fetchJson<MethodsResponse>(`/api/methods?${sp}`),
    staleTime: 60_000,
  });
}
